import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Calendar } from "@/components/ui/calendar";
import { 
  Pagination, 
  PaginationContent, 
  PaginationItem, 
  PaginationLink, 
  PaginationNext, 
  PaginationPrevious,
  PaginationEllipsis 
} from "@/components/ui/pagination";
import { ArrowLeft, Search, Users, DollarSign, Package, TrendingUp, Calendar as CalendarIcon, Filter, X, Download } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { format } from "date-fns";
import { US_STATES } from "@/lib/us-states";
import { cn } from "@/lib/utils";
import Papa from "papaparse";

interface UserData {
  id: string;
  email: string;
  full_name: string | null;
  phone: string;
  city: string | null;
  state: string | null;
  zip: string | null;
  address_line1: string | null;
  created_at: string;
  signup_source: string | null;
  use_case: string | null;
  billing_start_date: string | null;
  total_boxes: number;
  total_items: number;
  monthly_revenue: number;
  months_stored: number | null;
  status: 'active' | 'inactive' | 'never_stored';
  stored_boxes: number;
}

interface SummaryData {
  totalUsers: number;
  activeSubscribers: number;
  totalBoxes: number;
  totalMRR: number;
}

interface PaginationMeta {
  page: number;
  limit: number;
  totalCount: number;
  totalPages: number;
  hasNextPage: boolean;
  hasPrevPage: boolean;
}

const PAGE_SIZE = 25;

const AdminUsers = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [users, setUsers] = useState<UserData[]>([]);
  const [summary, setSummary] = useState<SummaryData | null>(null);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  
  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [pagination, setPagination] = useState<PaginationMeta | null>(null);
  
  // Filter states
  const [statusFilter, setStatusFilter] = useState<string>("all");
  const [stateFilter, setStateFilter] = useState<string>("all");
  const [sourceFilter, setSourceFilter] = useState<string>("all");
  const [dateFrom, setDateFrom] = useState<Date | undefined>();
  const [dateTo, setDateTo] = useState<Date | undefined>();
  const [hideTestAccounts, setHideTestAccounts] = useState(true);
  
  // Unique sources - keep a separate list that doesn't change with pagination
  const [allSources, setAllSources] = useState<string[]>([]);
  

  // Debounce search input
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchQuery);
      setCurrentPage(1); // Reset to page 1 on search
    }, 300);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  const fetchUsers = useCallback(async (page: number = 1) => {
    try {
      setLoading(true);
      const { data: { session } } = await supabase.auth.getSession();
      
      if (!session) {
        toast({
          title: "Authentication required",
          description: "Please log in to access this page",
          variant: "destructive",
        });
        navigate("/auth");
        return;
      }

      const { data, error } = await supabase.functions.invoke("admin-list-users", {
        body: {
          statusFilter,
          stateFilter: stateFilter !== "all" ? stateFilter : undefined,
          sourceFilter: sourceFilter !== "all" ? sourceFilter : undefined,
          dateFrom: dateFrom?.toISOString(),
          dateTo: dateTo?.toISOString(),
          search: debouncedSearch || undefined,
          page,
          limit: PAGE_SIZE,
          hideTestAccounts,
        },
        headers: {
          Authorization: `Bearer ${session.access_token}`,
        },
      });

      if (error) throw error;

      setUsers(data.users);
      setSummary(data.summary);
      setPagination(data.pagination);
      
      // Update sources list only if not filtered
      if (!sourceFilter || sourceFilter === "all") {
        const sources = data.users
          .map((u: UserData) => u.signup_source)
          .filter((s: string | null): s is string => Boolean(s));
        setAllSources(prev => {
          const combined = new Set([...prev, ...sources]);
          return Array.from(combined);
        });
      }
    } catch (error: any) {
      console.error("Error fetching users:", error);
      toast({
        title: "Error loading users",
        description: error.message || "Failed to load user data",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  }, [statusFilter, stateFilter, sourceFilter, dateFrom, dateTo, debouncedSearch, hideTestAccounts, navigate, toast]);

  // Initial fetch and refetch on filter/page changes
  useEffect(() => {
    fetchUsers(currentPage);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage, statusFilter, stateFilter, sourceFilter, dateFrom, dateTo, debouncedSearch, fetchUsers]);

  // Reset to page 1 when filters change (except page itself)
  useEffect(() => {
    setCurrentPage(1);
  }, [statusFilter, stateFilter, sourceFilter, dateFrom, dateTo]);

  const clearFilters = () => {
    setStatusFilter("all");
    setStateFilter("all");
    setSourceFilter("all");
    setDateFrom(undefined);
    setDateTo(undefined);
    setSearchQuery("");
    setHideTestAccounts(true);
    setCurrentPage(1);
  };

  const hasActiveFilters = 
    statusFilter !== "all" || 
    stateFilter !== "all" || 
    sourceFilter !== "all" || 
    dateFrom || 
    dateTo ||
    searchQuery.trim() !== "" ||
    !hideTestAccounts;

  const handleRowClick = (user: UserData) => {
    navigate(`/admin/users/${user.id}`);
  };

  const exportToCSV = async () => {
    // Fetch all users for export (no pagination)
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) return;

      toast({
        title: "Preparing export...",
        description: "Fetching all user data for export",
      });

      const { data, error } = await supabase.functions.invoke("admin-list-users", {
        body: {
          statusFilter,
          stateFilter: stateFilter !== "all" ? stateFilter : undefined,
          sourceFilter: sourceFilter !== "all" ? sourceFilter : undefined,
          dateFrom: dateFrom?.toISOString(),
          dateTo: dateTo?.toISOString(),
          search: debouncedSearch || undefined,
          page: 1,
          limit: 10000, // Get all for export
        },
        headers: {
          Authorization: `Bearer ${session.access_token}`,
        },
      });

      if (error) throw error;

      const exportData = data.users.map((user: UserData) => ({
        Email: user.email,
        Name: user.full_name || "",
        Phone: user.phone,
        Address: user.address_line1 || "",
        City: user.city || "",
        State: user.state || "",
        ZIP: user.zip || "",
        "Signup Date": format(new Date(user.created_at), "yyyy-MM-dd"),
        "Signup Source": user.signup_source || "organic",
        "Use Case": user.use_case || "",
        Status: user.status,
        "Total Boxes": user.total_boxes,
        "Stored Boxes": user.stored_boxes,
        "Total Items": user.total_items,
        "Monthly Revenue": user.monthly_revenue.toFixed(2),
        "Months Stored": user.months_stored ?? "",
        "Billing Start Date": user.billing_start_date ? format(new Date(user.billing_start_date), "yyyy-MM-dd") : "",
      }));

      const csv = Papa.unparse(exportData);
      const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", `users-export-${format(new Date(), "yyyy-MM-dd")}.csv`);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);

      toast({
        title: "Export complete",
        description: `Exported ${data.users.length} users to CSV`,
      });
    } catch (error: any) {
      toast({
        title: "Export failed",
        description: error.message || "Failed to export users",
        variant: "destructive",
      });
    }
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case "active":
        return <Badge className="bg-green-500">Active</Badge>;
      case "inactive":
        return <Badge variant="secondary">Inactive</Badge>;
      case "never_stored":
        return <Badge variant="outline">Never Stored</Badge>;
      default:
        return <Badge variant="outline">{status}</Badge>;
    }
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
    }).format(amount);
  };

  // Generate page numbers to display
  const getPageNumbers = () => {
    if (!pagination) return [];
    const { page, totalPages } = pagination;
    const pages: (number | 'ellipsis')[] = [];
    
    if (totalPages <= 7) {
      for (let i = 1; i <= totalPages; i++) pages.push(i);
    } else {
      if (page <= 4) {
        for (let i = 1; i <= 5; i++) pages.push(i);
        pages.push('ellipsis');
        pages.push(totalPages);
      } else if (page >= totalPages - 3) {
        pages.push(1);
        pages.push('ellipsis');
        for (let i = totalPages - 4; i <= totalPages; i++) pages.push(i);
      } else {
        pages.push(1);
        pages.push('ellipsis');
        for (let i = page - 1; i <= page + 1; i++) pages.push(i);
        pages.push('ellipsis');
        pages.push(totalPages);
      }
    }
    return pages;
  };

  return (
    <div className="container mx-auto py-8 px-4 max-w-7xl">
      <Button
        variant="ghost"
        onClick={() => navigate("/admin")}
        className="mb-6"
      >
        <ArrowLeft className="mr-2 h-4 w-4" />
        Back to Admin
      </Button>

      <div className="mb-8">
        <h1 className="text-4xl font-bold mb-2">User Management</h1>
        <p className="text-muted-foreground">
          Monitor and manage all users in the system
        </p>
      </div>

      {/* Summary Cards */}
      {loading && !summary ? (
        <div className="grid gap-4 md:grid-cols-4 mb-8">
          {[...Array(4)].map((_, i) => (
            <Card key={i}>
              <CardHeader className="pb-2">
                <Skeleton className="h-4 w-24" />
              </CardHeader>
              <CardContent>
                <Skeleton className="h-8 w-16" />
              </CardContent>
            </Card>
          ))}
        </div>
      ) : (
        <div className="grid gap-4 md:grid-cols-4 mb-8">
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium flex items-center gap-2">
                <Users className="h-4 w-4 text-muted-foreground" />
                Total Users
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{summary?.totalUsers || 0}</div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium flex items-center gap-2">
                <TrendingUp className="h-4 w-4 text-muted-foreground" />
                Active Subscribers
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{summary?.activeSubscribers || 0}</div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium flex items-center gap-2">
                <Package className="h-4 w-4 text-muted-foreground" />
                Total Boxes
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{summary?.totalBoxes || 0}</div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium flex items-center gap-2">
                <DollarSign className="h-4 w-4 text-muted-foreground" />
                Monthly Revenue
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">
                {formatCurrency(summary?.totalMRR || 0)}
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Filters */}
      <Card className="mb-6">
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="flex items-center gap-2">
                <Filter className="h-5 w-5" />
                Filters & Search
              </CardTitle>
              <CardDescription>
                Filter and search through users
              </CardDescription>
            </div>
            {hasActiveFilters && (
              <Button variant="ghost" size="sm" onClick={clearFilters}>
                <X className="h-4 w-4 mr-1" />
                Clear Filters
              </Button>
            )}
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Status Tabs */}
          <div>
            <label className="text-sm font-medium mb-2 block">Status</label>
            <Tabs value={statusFilter} onValueChange={setStatusFilter}>
              <TabsList className="grid w-full grid-cols-4">
                <TabsTrigger value="all">All</TabsTrigger>
                <TabsTrigger value="active">Active</TabsTrigger>
                <TabsTrigger value="inactive">Inactive</TabsTrigger>
                <TabsTrigger value="new_this_month">New This Month</TabsTrigger>
              </TabsList>
            </Tabs>
          </div>

          {/* Dropdowns Row */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* State Filter */}
            <div>
              <label className="text-sm font-medium mb-2 block">State</label>
              <Select value={stateFilter} onValueChange={setStateFilter}>
                <SelectTrigger>
                  <SelectValue placeholder="All states" />
                </SelectTrigger>
                <SelectContent className="bg-background">
                  <SelectItem value="all">All States</SelectItem>
                  {US_STATES.map((state) => (
                    <SelectItem key={state.value} value={state.value}>
                      {state.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Source Filter */}
            <div>
              <label className="text-sm font-medium mb-2 block">Signup Source</label>
              <Select value={sourceFilter} onValueChange={setSourceFilter}>
                <SelectTrigger>
                  <SelectValue placeholder="All sources" />
                </SelectTrigger>
                <SelectContent className="bg-background">
                  <SelectItem value="all">All Sources</SelectItem>
                  {allSources.map((source) => (
                    <SelectItem key={source} value={source}>
                      {source}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Date Range */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="text-sm font-medium mb-2 block">Signup Date From</label>
              <Popover>
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    className={cn(
                      "w-full justify-start text-left font-normal",
                      !dateFrom && "text-muted-foreground"
                    )}
                  >
                    <CalendarIcon className="mr-2 h-4 w-4" />
                    {dateFrom ? format(dateFrom, "PPP") : "Pick a date"}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0 bg-background" align="start">
                  <Calendar
                    mode="single"
                    selected={dateFrom}
                    onSelect={setDateFrom}
                    initialFocus
                  />
                </PopoverContent>
              </Popover>
            </div>

            <div>
              <label className="text-sm font-medium mb-2 block">Signup Date To</label>
              <Popover>
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    className={cn(
                      "w-full justify-start text-left font-normal",
                      !dateTo && "text-muted-foreground"
                    )}
                  >
                    <CalendarIcon className="mr-2 h-4 w-4" />
                    {dateTo ? format(dateTo, "PPP") : "Pick a date"}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0 bg-background" align="start">
                  <Calendar
                    mode="single"
                    selected={dateTo}
                    onSelect={setDateTo}
                    initialFocus
                  />
                </PopoverContent>
              </Popover>
            </div>
          </div>

          {/* Test Accounts Toggle */}
          <div className="flex items-center justify-between">
            <div className="space-y-0.5">
              <label className="text-sm font-medium">Hide test accounts</label>
              <p className="text-xs text-muted-foreground">
                Emails with + addressing (e.g., user+test@email.com)
              </p>
            </div>
            <Switch
              checked={hideTestAccounts}
              onCheckedChange={setHideTestAccounts}
            />
          </div>

          {/* Search */}
          <div>
            <label className="text-sm font-medium mb-2 block">Search</label>
            <div className="relative">
              <Search className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search by email, name, city, state, or phone..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10"
              />
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Users Table */}
      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <div>
            <CardTitle>
              Users {pagination ? `(${pagination.totalCount})` : ""}
            </CardTitle>
            {pagination && pagination.totalCount > 0 && (
              <CardDescription>
                Showing {((pagination.page - 1) * PAGE_SIZE) + 1}-{Math.min(pagination.page * PAGE_SIZE, pagination.totalCount)} of {pagination.totalCount}
              </CardDescription>
            )}
          </div>
          <Button 
            onClick={exportToCSV} 
            variant="outline" 
            size="sm"
            disabled={!pagination || pagination.totalCount === 0}
          >
            <Download className="h-4 w-4 mr-2" />
            Export CSV
          </Button>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="space-y-2">
              {[...Array(5)].map((_, i) => (
                <Skeleton key={i} className="h-16 w-full" />
              ))}
            </div>
          ) : users.length === 0 ? (
            <div className="text-center py-12">
              <p className="text-muted-foreground">No users found</p>
            </div>
          ) : (
            <>
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Email</TableHead>
                      <TableHead>Name</TableHead>
                      <TableHead>Location</TableHead>
                      <TableHead>Signup Date</TableHead>
                      <TableHead className="text-right">Boxes</TableHead>
                      <TableHead className="text-right">Stored</TableHead>
                      <TableHead className="text-right">Items</TableHead>
                      <TableHead className="text-right">Monthly $</TableHead>
                      <TableHead className="text-right">Months</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>Source</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {users.map((user) => (
                      <TableRow 
                        key={user.id} 
                        className="cursor-pointer hover:bg-muted/50"
                        onClick={() => handleRowClick(user)}
                      >
                        <TableCell className="font-medium">{user.email}</TableCell>
                        <TableCell>{user.full_name || "-"}</TableCell>
                        <TableCell>
                          {user.city && user.state
                            ? `${user.city}, ${user.state}`
                            : user.state || user.city || "-"}
                        </TableCell>
                        <TableCell>
                          {format(new Date(user.created_at), "MMM d, yyyy")}
                        </TableCell>
                        <TableCell className="text-right">{user.total_boxes}</TableCell>
                        <TableCell className="text-right">{user.stored_boxes}</TableCell>
                        <TableCell className="text-right">{user.total_items}</TableCell>
                        <TableCell className="text-right">
                          {formatCurrency(user.monthly_revenue)}
                        </TableCell>
                        <TableCell className="text-right">
                          {user.months_stored !== null ? user.months_stored : "-"}
                        </TableCell>
                        <TableCell>{getStatusBadge(user.status)}</TableCell>
                        <TableCell>
                          <Badge variant="outline">
                            {user.signup_source || "organic"}
                          </Badge>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>

              {/* Pagination */}
              {pagination && pagination.totalPages > 1 && (
                <div className="mt-6 flex items-center justify-between">
                  <p className="text-sm text-muted-foreground">
                    Page {pagination.page} of {pagination.totalPages}
                  </p>
                  <Pagination>
                    <PaginationContent>
                      <PaginationItem>
                        <PaginationPrevious 
                          onClick={() => pagination.hasPrevPage && setCurrentPage(p => p - 1)}
                          className={cn(!pagination.hasPrevPage && "pointer-events-none opacity-50")}
                        />
                      </PaginationItem>
                      
                      {getPageNumbers().map((pageNum, idx) => (
                        <PaginationItem key={idx}>
                          {pageNum === 'ellipsis' ? (
                            <PaginationEllipsis />
                          ) : (
                            <PaginationLink
                              onClick={() => setCurrentPage(pageNum)}
                              isActive={pageNum === pagination.page}
                            >
                              {pageNum}
                            </PaginationLink>
                          )}
                        </PaginationItem>
                      ))}
                      
                      <PaginationItem>
                        <PaginationNext 
                          onClick={() => pagination.hasNextPage && setCurrentPage(p => p + 1)}
                          className={cn(!pagination.hasNextPage && "pointer-events-none opacity-50")}
                        />
                      </PaginationItem>
                    </PaginationContent>
                  </Pagination>
                </div>
              )}
            </>
          )}
        </CardContent>
      </Card>

    </div>
  );
};

export default AdminUsers;
